import React, { useState, useEffect } from "react"
import { Col, Row, Spinner, Table } from "reactstrap"
import api from "API/Api"
import { AvForm, AvField } from "availity-reactstrap-validation"
import jsPDF from "jspdf"
import "jspdf-autotable"
import { usePermissions } from "API/PermissionContext"

const FundsReport = () => {
  const { canExport, MODULE } = usePermissions()
  const MID = MODULE.REPORTS
  const [loading, setLoading] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [results, setResults] = useState(null)
  const [fundTypes, setFundTypes] = useState([])
  const [displayMode, setDisplayMode] = useState("")
  const [accountType, setAccountType] = useState("")
  const [search, setSearch] = useState("")

  useEffect(() => { loadFundTypes() }, [])
  useEffect(() => { setSubmitted(false); setResults(null) }, [accountType, displayMode])

  const loadFundTypes = async () => {
    try {
      const resp = await api.get("fundtypes")
      setFundTypes(Array.isArray(resp.data.data) ? resp.data.data : resp.data.data?.data || [])
    } catch (e) { console.error(e) }
  }

  const handleSubmit = async (e, v) => {
    setLoading(true)
    setDisplayMode(v.display)
    setAccountType(v.account)
    try {
      const resp = await api.post("reports/funds", v)
      if (resp.data.success) {
        setResults(resp.data.data)
        setSubmitted(true)
      } else {
        setResults(null)
        setSubmitted(true)
      }
    } catch (err) {
      console.error(err)
      setResults(null)
      setSubmitted(true)
    }
    setLoading(false)
  }

  const fmt = n => {
    const v = parseFloat(n) || 0
    return v.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
  }

  const downloadPdf = () => {
    const pdf = new jsPDF("l")
    pdf.setFontSize(14)
    pdf.text("Funds Report", 14, 15)
    pdf.autoTable({ html: "#fundsTbl", startY: 22, styles: { fontSize: 7 } })
    pdf.save("Funds-Report.pdf")
  }

  const downloadCsv = () => {
    if (!results) return
    let csv = ""
    if (displayMode === "transactions" && Array.isArray(results)) {
      csv = "S.No,Type,Date,Name,Chilling Center,Route,Center,Amount,Deduction,Remarks\n"
      results.forEach((r, i) => {
        csv += `${i + 1},"${r.type}","${r.date}","${r.entityName || ''}","${r.ccName || ''}","${r.routeName || ''}","${r.centerName || ''}","${r.amount}","${r.deduction}","${r.remarks || ''}"\n`
      })
    } else if (displayMode === "balance_summary" && Array.isArray(results)) {
      csv = "S.No,Name,Cash Advance,Cash Deduction,Loan,Loan Deduction,Material,Material Deduction,Recovery,Balance\n"
      results.forEach((r, i) => {
        csv += `${i + 1},"${r.entityName}","${r.cashAdvance}","${r.cashDeduction}","${r.loanAmount}","${r.loanDeduction}","${r.materialAdvance}","${r.materialDeduction}","${r.recoveryAmount}","${r.balance}"\n`
      })
    } else if (displayMode === "total_amount" && results) {
      csv = "Category,Amount,Deduction,Outstanding\n"
      csv += `"Cash Advance","${results.cashAdvance}","${results.cashDeduction}","${(parseFloat(results.cashAdvance) - parseFloat(results.cashDeduction)).toFixed(2)}"\n`
      csv += `"Loan","${results.loanAmount}","${results.loanDeduction}","${(parseFloat(results.loanAmount) - parseFloat(results.loanDeduction)).toFixed(2)}"\n`
      csv += `"Material Advance","${results.materialAdvance}","${results.materialDeduction}","${(parseFloat(results.materialAdvance) - parseFloat(results.materialDeduction)).toFixed(2)}"\n`
      csv += `"Recovery","${results.recoveryAmount}","0",""\n`
      csv += `"Total Outstanding","","","${results.totalOutstanding}"\n`
    }
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" })
    const link = document.createElement("a")
    link.href = URL.createObjectURL(blob)
    link.download = "Funds-Report.csv"
    link.click()
  }

  // Transactions table
  const renderTransactions = () => {
    const data = Array.isArray(results) ? results : []
    const filtered = search
      ? data.filter(r =>
          (r.type || "").toLowerCase().includes(search.toLowerCase()) ||
          (r.entityName || "").toLowerCase().includes(search.toLowerCase()) ||
          (r.ccName || "").toLowerCase().includes(search.toLowerCase()) ||
          (r.routeName || "").toLowerCase().includes(search.toLowerCase()) ||
          (r.centerName || "").toLowerCase().includes(search.toLowerCase())
        )
      : data
    return (
      <>
        <Row className="mb-2 notToPrint">
          <Col md={9}><span className="text-muted">Showing {filtered.length} of {data.length} entries</span></Col>
          <Col md={3}><input type="text" className="form-control form-control-sm" placeholder="Search..." value={search} onChange={e => setSearch(e.target.value)} /></Col>
        </Row>
        <div className="table-responsive">
          <Table id="fundsTbl" className="dairyTbl" size="sm" bordered>
            <thead>
              <tr><th>#</th><th>Type</th><th>Date</th><th>Name</th><th>Chilling Center</th><th>Route</th><th>Center</th><th className="text-end">Amount</th><th className="text-end">Deduction</th><th>Remarks</th></tr>
            </thead>
            <tbody>
              {filtered.length > 0 ? filtered.map((r, i) => (
                <tr key={i}>
                  <td>{i + 1}</td>
                  <td><span className={`badge ${r.type.includes("Recovery") ? "bg-warning" : r.type.includes("Loan") ? "bg-info" : r.type.includes("Material") ? "bg-secondary" : "bg-primary"}`}>{r.type}</span></td>
                  <td>{r.date}</td>
                  <td>{r.entityName || '-'}</td>
                  <td>{r.ccName || '-'}</td>
                  <td>{r.routeName || '-'}</td>
                  <td>{r.centerName || '-'}</td>
                  <td className="text-end">{fmt(r.amount)}</td>
                  <td className="text-end">{fmt(r.deduction)}</td>
                  <td>{r.remarks || '-'}</td>
                </tr>
              )) : <tr><td colSpan="10" className="text-center py-3">No Transactions Found</td></tr>}
            </tbody>
            {filtered.length > 0 && <tfoot>
              <tr style={{ fontWeight: 700, background: "#f1f5f9" }}>
                <td colSpan="7">Total</td>
                <td className="text-end">{fmt(filtered.reduce((s, r) => s + (parseFloat(r.amount) || 0), 0))}</td>
                <td className="text-end">{fmt(filtered.reduce((s, r) => s + (parseFloat(r.deduction) || 0), 0))}</td>
                <td></td>
              </tr>
            </tfoot>}
          </Table>
        </div>
      </>
    )
  }

  // Balance Summary table
  const renderBalanceSummary = () => {
    const data = Array.isArray(results) ? results : []
    const filtered = search
      ? data.filter(r => (r.entityName || "").toLowerCase().includes(search.toLowerCase()))
      : data
    return (
      <>
        <Row className="mb-2 notToPrint">
          <Col md={9}><span className="text-muted">Showing {filtered.length} of {data.length} entries</span></Col>
          <Col md={3}><input type="text" className="form-control form-control-sm" placeholder="Search..." value={search} onChange={e => setSearch(e.target.value)} /></Col>
        </Row>
        <div className="table-responsive">
          <Table id="fundsTbl" className="dairyTbl" size="sm" bordered>
            <thead>
              <tr>
                <th rowSpan="2">#</th><th rowSpan="2">Name</th>
                <th colSpan="2" className="text-center" style={{ background: "#e3f2fd" }}>Cash Advance</th>
                <th colSpan="2" className="text-center" style={{ background: "#f3e5f5" }}>Loan</th>
                <th colSpan="2" className="text-center" style={{ background: "#fff3e0" }}>Material</th>
                <th rowSpan="2" className="text-center" style={{ background: "#fce4ec" }}>Recovery</th>
                <th rowSpan="2" className="text-center" style={{ background: "#e8f5e9" }}>Balance</th>
              </tr>
              <tr>
                <th style={{ background: "#e3f2fd" }}>Amount</th><th style={{ background: "#e3f2fd" }}>Deducted</th>
                <th style={{ background: "#f3e5f5" }}>Amount</th><th style={{ background: "#f3e5f5" }}>Deducted</th>
                <th style={{ background: "#fff3e0" }}>Amount</th><th style={{ background: "#fff3e0" }}>Deducted</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length > 0 ? filtered.map((r, i) => (
                <tr key={i}>
                  <td>{i + 1}</td>
                  <td>{r.entityName}</td>
                  <td className="text-end">{fmt(r.cashAdvance)}</td>
                  <td className="text-end">{fmt(r.cashDeduction)}</td>
                  <td className="text-end">{fmt(r.loanAmount)}</td>
                  <td className="text-end">{fmt(r.loanDeduction)}</td>
                  <td className="text-end">{fmt(r.materialAdvance)}</td>
                  <td className="text-end">{fmt(r.materialDeduction)}</td>
                  <td className="text-end">{fmt(r.recoveryAmount)}</td>
                  <td className="text-end" style={{ fontWeight: 600, color: parseFloat(r.balance) > 0 ? "#dc3545" : "#198754" }}>{fmt(r.balance)}</td>
                </tr>
              )) : <tr><td colSpan="10" className="text-center py-3">No Records Found</td></tr>}
            </tbody>
            {filtered.length > 0 && <tfoot>
              <tr style={{ fontWeight: 700, background: "#f1f5f9" }}>
                <td colSpan="2">Total</td>
                <td className="text-end">{fmt(filtered.reduce((s, r) => s + (parseFloat(r.cashAdvance) || 0), 0))}</td>
                <td className="text-end">{fmt(filtered.reduce((s, r) => s + (parseFloat(r.cashDeduction) || 0), 0))}</td>
                <td className="text-end">{fmt(filtered.reduce((s, r) => s + (parseFloat(r.loanAmount) || 0), 0))}</td>
                <td className="text-end">{fmt(filtered.reduce((s, r) => s + (parseFloat(r.loanDeduction) || 0), 0))}</td>
                <td className="text-end">{fmt(filtered.reduce((s, r) => s + (parseFloat(r.materialAdvance) || 0), 0))}</td>
                <td className="text-end">{fmt(filtered.reduce((s, r) => s + (parseFloat(r.materialDeduction) || 0), 0))}</td>
                <td className="text-end">{fmt(filtered.reduce((s, r) => s + (parseFloat(r.recoveryAmount) || 0), 0))}</td>
                <td className="text-end" style={{ fontWeight: 700 }}>{fmt(filtered.reduce((s, r) => s + (parseFloat(r.balance) || 0), 0))}</td>
              </tr>
            </tfoot>}
          </Table>
        </div>
      </>
    )
  }

  // Total Amount summary cards
  const renderTotalAmount = () => {
    if (!results || Array.isArray(results)) return <p className="text-center py-3">No Data</p>
    const rows = [
      { label: "Cash Advance", amount: results.cashAdvance, deduction: results.cashDeduction },
      { label: "Loan", amount: results.loanAmount, deduction: results.loanDeduction },
      { label: "Material Advance", amount: results.materialAdvance, deduction: results.materialDeduction },
      { label: "Recovery", amount: results.recoveryAmount, deduction: 0 },
    ]
    return (
      <div className="table-responsive">
        <Table id="fundsTbl" className="dairyTbl" size="sm" bordered>
          <thead>
            <tr><th>Category</th><th className="text-end">Total Amount</th><th className="text-end">Deducted</th><th className="text-end">Outstanding</th></tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i}>
                <td style={{ fontWeight: 600 }}>{r.label}</td>
                <td className="text-end">{fmt(r.amount)}</td>
                <td className="text-end">{fmt(r.deduction)}</td>
                <td className="text-end">{r.label === "Recovery" ? "-" : fmt((parseFloat(r.amount) || 0) - (parseFloat(r.deduction) || 0))}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr style={{ fontWeight: 700, background: "#f1f5f9", fontSize: "1.05em" }}>
              <td>Total Outstanding Balance</td>
              <td></td><td></td>
              <td className="text-end" style={{ color: parseFloat(results.totalOutstanding) > 0 ? "#dc3545" : "#198754" }}>{fmt(results.totalOutstanding)}</td>
            </tr>
          </tfoot>
        </Table>
      </div>
    )
  }

  const renderResults = () => {
    if (!submitted) return null
    if (displayMode === "transactions") return renderTransactions()
    if (displayMode === "balance_summary") return renderBalanceSummary()
    if (displayMode === "total_amount") return renderTotalAmount()
    return null
  }

  return (
    <div className="dashBody">
      <div className="font-size-18 mb-0 dashHeader notToPrint"><Row><Col md={12}><h4>Funds Report</h4></Col></Row></div>
      <div className="dashContent">
        <AvForm className="form-horizontal notToPrint" onValidSubmit={(e, v) => handleSubmit(e, v)}>
          <Row>
            <Col md={3}>
              <div className="mb-3">
                <AvField type="select" name="account" label="Account *" required>
                  <option value="">Select</option>
                  <option value="company">Company</option>
                  <option value="vendor">Vendor</option>
                  <option value="producer">Producer</option>
                </AvField>
              </div>
            </Col>
            <Col md={3}>
              <div className="mb-3">
                <AvField type="select" name="fundType" label="Fund Type">
                  <option value="">All</option>
                  {fundTypes.map((f, i) => <option key={i} value={f.id}>{f.fund_type_name}</option>)}
                </AvField>
              </div>
            </Col>
            <Col md={3}>
              <div className="mb-3">
                <AvField type="select" name="display" label="Display *" required>
                  <option value="">Select</option>
                  <option value="balance_summary">Balance Summary</option>
                  <option value="total_amount">Total Amount</option>
                  <option value="transactions">Transactions</option>
                </AvField>
              </div>
            </Col>
            <Col md={3} className="d-flex align-items-end pb-3">
              <button className="btn btn-primary w-md waves-effect waves-light" type="submit" disabled={loading}>
                {loading ? <><Spinner animation="border" size="sm" /><span style={{ paddingLeft: "10px" }}>Submitting...</span></> : <><i className="fas fa-check me-1"></i>Submit</>}
              </button>
            </Col>
          </Row>
        </AvForm>

        {submitted && <>
          {canExport(MID) && <div className="exportBtns notToPrint mb-2">
            <button className="btn btn-info btn-sm m-1" onClick={downloadPdf}><i className="fas fa-file-pdf me-1"></i>PDF</button>
            <button className="btn btn-info btn-sm m-1" onClick={() => window.print()}><i className="fas fa-print me-1"></i>Print</button>
            <button className="btn btn-info btn-sm m-1" onClick={downloadCsv}><i className="fas fa-file-csv me-1"></i>CSV</button>
          </div>}
          {renderResults()}
        </>}
      </div>
    </div>
  )
}

export default FundsReport
